import { useState } from "react";
import { CompanyPreferences } from "@type/company";

// ─── Design Tokens ───────────────────────────────────────────────────────────
const T = {
    primary500: "#2563EB",
    primary400: "#3B82F6",
    primary100: "#DBEAFE",
    primary50: "#EFF6FF",
    neutral900: "#111827",
    neutral400: "#9CA3AF",
    neutral600: "#4B5563",
    neutral200: "#E5E7EB",
    neutral50: "#F8FAFC",
    white: "#FFFFFF",
    overlay: "rgba(0,0,0,0.5)",
};

// ─── Icon primitives ─────────────────────────────────────────────────────────
function Icon({ d, size = 16, extra = null }: { d: React.ReactNode; size?: number; extra?: React.ReactNode }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
            stroke={T.primary500} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            {d}
            {extra}
        </svg>
    );
}

// ─── Step Indicator Dots ─────────────────────────────────────────────────────
function StepDots({ active }: { active: number }) {
    return (
        <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
            {[0, 1].map(i => (
                <div key={i} style={{
                    width: i === active ? 16 : 6,
                    height: 6,
                    borderRadius: 9999,
                    background: i === active ? T.primary500 : T.neutral200,
                    transition: "all 0.2s ease",
                }} />
            ))}
        </div>
    );
}

// ─── Localized content ───────────────────────────────────────────────────────
type LanguageKey = "English" | "Vietnamese" | "FirstMove" | "Demo" | "id-ID" | "ms-MY" | "Training";

interface LocalizedContent {
    consentTitle: string;
    consentStepLabel: string;
    consentMessage: string;
    consentSubMessage: string;
    cancelButton: string;
    continueButton: string;
    continueButtonShort: string;
    continuingButton: string;
    tipsTitle: string;
    tipsStepLabel: string;
    tipsHeader: string;
    tip1Title: string;
    tip1Desc: string;
    tip2Title: string;
    tip2Desc: string;
    tip3Title: string;
    tip3Desc: string;
    backButton: string;
    startButton: string;
    startingButton: string;
}

function getContent(language: LanguageKey, companyName: string): LocalizedContent {
    const contents: Record<string, LocalizedContent> = {
        English: {
            consentTitle: "Quick Reminder",
            consentStepLabel: "Step 1 of 2 · Please read before continuing",
            consentMessage: `This AI interview will be recorded and reviewed by ${companyName} for recruitment purposes.`,
            consentSubMessage: "By continuing, you consent to the use of your interview data for this hiring process.",
            cancelButton: "Cancel",
            continueButton: "I Understand, Continue →",
            continueButtonShort: "Continue →",
            continuingButton: "Continuing…",
            tipsTitle: "You're almost in",
            tipsStepLabel: "Step 2 of 2 · Quick tips for the best experience",
            tipsHeader: "Tips for a better interview experience",
            tip1Title: "Keep answers to 30–60 seconds",
            tip1Desc: "Pause naturally after each point so ourteam AI knows you've finished a thought before you continue.",
            tip2Title: "Wait for questions to finish",
            tip2Desc: "Let ourteam AI fully complete its question before responding. Jumping in early may cut off part of the question.",
            tip3Title: "Take a breath, you've got this",
            tip3Desc: "There are no trick questions and no single right answer. If you need a moment to think, take it.",
            backButton: "← Back",
            startButton: "Start Interview",
            startingButton: "Starting interview…",
        },
        Vietnamese: {
            consentTitle: "Lưu ý nhanh",
            consentStepLabel: "Bước 1/2 · Vui lòng đọc trước khi tiếp tục",
            consentMessage: `Cuộc phỏng vấn AI này sẽ được ghi lại và xem xét bởi ${companyName} cho mục đích tuyển dụng.`,
            consentSubMessage: "Khi tiếp tục, bạn đồng ý cho việc sử dụng dữ liệu phỏng vấn của bạn cho quy trình tuyển dụng này.",
            cancelButton: "Hủy bỏ",
            continueButton: "Tôi hiểu, Tiếp tục →",
            continueButtonShort: "Tiếp tục →",
            continuingButton: "Đang tiếp tục…",
            tipsTitle: "Bạn sắp bắt đầu",
            tipsStepLabel: "Bước 2/2 · Mẹo để có trải nghiệm tốt nhất",
            tipsHeader: "Mẹo để phỏng vấn tốt hơn",
            tip1Title: "Giữ câu trả lời trong 30–60 giây",
            tip1Desc: "Dừng lại tự nhiên sau mỗi ý để ourteam AI biết bạn đã hoàn thành suy nghĩ trước khi tiếp tục.",
            tip2Title: "Đợi câu hỏi kết thúc",
            tip2Desc: "Hãy để ourteam AI hoàn thành câu hỏi trước khi trả lời. Nói sớm có thể làm gián đoạn câu hỏi.",
            tip3Title: "Hít thở sâu, bạn làm được",
            tip3Desc: "Không có câu hỏi mẹo và không có câu trả lời đúng duy nhất. Nếu cần thời gian suy nghĩ, hãy dành thời gian.",
            backButton: "← Quay lại",
            startButton: "Bắt đầu Phỏng vấn",
            startingButton: "Đang bắt đầu…",
        },
        "id-ID": {
            consentTitle: "Pengingat Singkat",
            consentStepLabel: "Langkah 1 dari 2 · Silakan baca sebelum melanjutkan",
            consentMessage: `Wawancara AI ini akan direkam dan ditinjau oleh ${companyName} untuk tujuan rekrutmen.`,
            consentSubMessage: "Dengan melanjutkan, Anda menyetujui penggunaan data wawancara Anda untuk proses perekrutan ini.",
            cancelButton: "Batal",
            continueButton: "Saya Mengerti, Lanjutkan →",
            continueButtonShort: "Lanjutkan →",
            continuingButton: "Melanjutkan…",
            tipsTitle: "Hampir siap",
            tipsStepLabel: "Langkah 2 dari 2 · Tips untuk pengalaman terbaik",
            tipsHeader: "Tips untuk wawancara yang lebih baik",
            tip1Title: "Jawab dalam 30–60 detik",
            tip1Desc: "Berhenti sejenak setelah setiap poin agar ourteam AI tahu Anda telah menyelesaikan pemikiran.",
            tip2Title: "Tunggu pertanyaan selesai",
            tip2Desc: "Biarkan ourteam AI menyelesaikan pertanyaan sebelum menjawab. Menjawab terlalu cepat dapat memotong pertanyaan.",
            tip3Title: "Tarik napas, Anda bisa",
            tip3Desc: "Tidak ada pertanyaan jebakan dan tidak ada jawaban benar tunggal. Jika perlu waktu berpikir, ambillah.",
            backButton: "← Kembali",
            startButton: "Mulai Wawancara",
            startingButton: "Memulai…",
        },
        "ms-MY": {
            consentTitle: "Peringatan Ringkas",
            consentStepLabel: "Langkah 1 daripada 2 · Sila baca sebelum meneruskan",
            consentMessage: `Temuduga AI ini akan dirakam dan disemak oleh ${companyName} untuk tujuan pengambilan pekerja.`,
            consentSubMessage: "Dengan meneruskan, anda bersetuju dengan penggunaan data temuduga anda untuk proses pengambilan ini.",
            cancelButton: "Batal",
            continueButton: "Saya Faham, Teruskan →",
            continueButtonShort: "Teruskan →",
            continuingButton: "Meneruskan…",
            tipsTitle: "Hampir masuk",
            tipsStepLabel: "Langkah 2 daripada 2 · Petua untuk pengalaman terbaik",
            tipsHeader: "Petua untuk temuduga yang lebih baik",
            tip1Title: "Jawab dalam 30–60 saat",
            tip1Desc: "Berhenti secara semula jadi selepas setiap poin supaya ourteam AI tahu anda telah selesai berfikir.",
            tip2Title: "Tunggu soalan selesai",
            tip2Desc: "Biarkan ourteam AI menyelesaikan soalan sebelum menjawab. Menjawab awal boleh memotong soalan.",
            tip3Title: "Tarik nafas, anda boleh",
            tip3Desc: "Tiada soalan perangkap dan tiada jawapan betul tunggal. Jika perlu masa berfikir, ambillah.",
            backButton: "← Kembali",
            startButton: "Mula Temuduga",
            startingButton: "Memulakan…",
        },
        FirstMove: {
            consentTitle: "Quick Reminder",
            consentStepLabel: "Step 1 of 2 · Please read before continuing",
            consentMessage: `This AI interview will be recorded and reviewed by ${companyName} for recruitment purposes.`,
            consentSubMessage: "By continuing, you consent to the use of your interview data for this hiring process.",
            cancelButton: "Cancel",
            continueButton: "I Understand, Continue →",
            continueButtonShort: "Continue →",
            continuingButton: "Continuing…",
            tipsTitle: "You're almost in",
            tipsStepLabel: "Step 2 of 2 · Quick tips for the best experience",
            tipsHeader: "Tips for a better interview experience",
            tip1Title: "Keep answers to 30–60 seconds",
            tip1Desc: "Pause naturally after each point so the AI knows you've finished a thought before you continue.",
            tip2Title: "Wait for questions to finish",
            tip2Desc: "Let the AI fully complete its question before responding. Jumping in early may cut off part of the question.",
            tip3Title: "Take a breath, you've got this",
            tip3Desc: "There are no trick questions and no single right answer. If you need a moment to think, take it.",
            backButton: "← Back",
            startButton: "Start Interview",
            startingButton: "Starting interview…",
        },
        Demo: {
            consentTitle: "Quick Reminder",
            consentStepLabel: "Step 1 of 2 · Please read before continuing",
            consentMessage: `This is a demo AI interview. It will be recorded for demonstration purposes.`,
            consentSubMessage: "By continuing, you consent to the use of your interview data for this demo.",
            cancelButton: "Cancel",
            continueButton: "I Understand, Continue →",
            continueButtonShort: "Continue →",
            continuingButton: "Continuing…",
            tipsTitle: "You're almost in",
            tipsStepLabel: "Step 2 of 2 · Quick tips for the best experience",
            tipsHeader: "Tips for a better interview experience",
            tip1Title: "Keep answers to 30–60 seconds",
            tip1Desc: "Pause naturally after each point so the AI knows you've finished a thought before you continue.",
            tip2Title: "Wait for questions to finish",
            tip2Desc: "Let the AI fully complete its question before responding. Jumping in early may cut off part of the question.",
            tip3Title: "Take a breath, you've got this",
            tip3Desc: "There are no trick questions and no single right answer. If you need a moment to think, take it.",
            backButton: "← Back",
            startButton: "Start Interview",
            startingButton: "Starting…",
        },
        Training: {
            consentTitle: "Quick Reminder",
            consentStepLabel: "Step 1 of 2 · Please read before continuing",
            consentMessage: `This roleplay session will be recorded and visible to your trainer. It will be kept as a training record.`,
            consentSubMessage: "By continuing, you consent to this recording being used for training purposes only.",
            cancelButton: "Cancel",
            continueButton: "I Understand, Continue →",
            continueButtonShort: "Continue →",
            continuingButton: "Continuing…",
            tipsTitle: "You're almost in",
            tipsStepLabel: "Step 2 of 2 · Quick tips for the best experience",
            tipsHeader: "Tips for a better roleplay experience",
            tip1Title: "Stay in character",
            tip1Desc: "Respond as if this were a real call. The closer you stay to character, the more useful the coaching feedback will be.",
            tip2Title: "Wait for the persona to finish speaking",
            tip2Desc: "Let your roleplay partner finish each line before you respond. Jumping in early may cut off part of what they said.",
            tip3Title: "Take a breath, you've got this",
            tip3Desc: "There are no trick prompts. If you need a moment to think, take it.",
            backButton: "← Back",
            startButton: "Start Roleplay",
            startingButton: "Starting roleplay…",
        },
    };

    return contents[language] || contents.English;
}

// ─── Step 1: Consent ─────────────────────────────────────────────────────────
function ConsentStep({ content, onCancel, onAgree }: {
    content: LocalizedContent;
    onCancel: () => void;
    onAgree: () => void;
}) {
    const [loading, setLoading] = useState(false);

    const handleAgree = () => {
        if (loading) return;
        setLoading(true);
        setTimeout(() => onAgree(), 120);
    };

    return (
        <>
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                <div style={styles.iconBox}>
                    <Icon d={<>
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                    </>} />
                </div>
                <div style={{ flex: 1 }}>
                    <div style={styles.modalTitle}>{content.consentTitle}</div>
                    <div style={styles.stepLabel}>{content.consentStepLabel}</div>
                </div>
                <StepDots active={0} />
            </div>

            <div style={styles.divider} />

            {/* Consent copy */}
            <div style={{ margin: "20px 0 24px", display: "flex", flexDirection: "column" as const, gap: 12 }}>
                <p style={{ fontSize: 13.5, fontWeight: 600, color: T.neutral900, lineHeight: 1.5, margin: 0 }}>
                    {content.consentMessage}
                </p>
                <p style={{ fontSize: 13, color: T.neutral600, lineHeight: 1.6, margin: 0 }}>
                    {content.consentSubMessage}
                </p>
            </div>

            {/* Actions */}
            <div style={{ display: "flex", gap: 10 }}>
                <button
                    onClick={onCancel}
                    style={styles.btnSecondary}
                    onMouseEnter={e => Object.assign(e.currentTarget.style, styles.btnSecondaryHover)}
                    onMouseLeave={e => Object.assign(e.currentTarget.style, styles.btnSecondaryBase)}
                >
                    {content.cancelButton}
                </button>
                <button
                    onClick={handleAgree}
                    disabled={loading}
                    style={{
                        ...styles.btnPrimary,
                        flex: 2,
                        opacity: loading ? 0.7 : 1,
                        cursor: loading ? "default" : "pointer",
                    }}
                    onMouseEnter={e => { if (!loading) e.currentTarget.style.background = T.primary400; }}
                    onMouseLeave={e => { if (!loading) e.currentTarget.style.background = T.primary500; }}
                >
                    {loading ? content.continuingButton : (
                        <>
                            <span className="hidden sm:inline">{content.continueButton}</span>
                            <span className="inline sm:hidden">{content.continueButtonShort}</span>
                        </>
                    )}
                </button>
            </div>
        </>
    );
}

// ─── Step 2: Speaking Tips ────────────────────────────────────────────────────
function TipsStep({ content, loading, onStart, onBack }: {
    content: LocalizedContent;
    loading?: boolean;
    onStart: () => void;
    onBack: () => void;
}) {
    const tips = [
        {
            icon: <Icon d={<>
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
            </>} />,
            title: content.tip1Title,
            desc: content.tip1Desc,
        },
        {
            icon: <Icon d={<>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <circle cx="12" cy="12" r="10" />
            </>}
                extra={<circle cx="12" cy="17" r=".5" fill={T.primary500} />} />,
            title: content.tip2Title,
            desc: content.tip2Desc,
        },
        {
            icon: <Icon d={<>
                <circle cx="12" cy="12" r="10" />
                <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                <line x1="9" y1="9" x2="9.01" y2="9" />
                <line x1="15" y1="9" x2="15.01" y2="9" />
            </>} />,
            title: content.tip3Title,
            desc: content.tip3Desc,
        },
    ];

    return (
        <>
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                <div style={styles.iconBox}>
                    <Icon d={<>
                        <path d="M9 21h6" />
                        <path d="M12 2a7 7 0 0 1 5 11.9V17a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1v-3.1A7 7 0 0 1 12 2z" />
                    </>} />
                </div>
                <div style={{ flex: 1 }}>
                    <div style={styles.modalTitle}>{content.tipsTitle}</div>
                    <div style={styles.stepLabel}>{content.tipsStepLabel}</div>
                </div>
                <StepDots active={1} />
            </div>

            <div style={styles.divider} />

            {/* Tips list */}
            <div style={{ display: "flex", flexDirection: "column" as const, gap: 14, margin: "20px 0" }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.neutral400, textTransform: "uppercase" as const, letterSpacing: "0.06em", marginBottom: 2 }}>
                    {content.tipsHeader}
                </div>
                {tips.map((tip, i) => (
                    <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                        <div style={styles.iconBox}>{tip.icon}</div>
                        <div style={{ paddingTop: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: T.neutral900, marginBottom: 2 }}>
                                {tip.title}
                            </div>
                            <div style={{ fontSize: 12.5, color: T.neutral600, lineHeight: 1.55 }}>
                                {tip.desc}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div style={styles.divider} />

            {/* Actions */}
            <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                <button
                    onClick={onBack}
                    style={styles.btnSecondary}
                    onMouseEnter={e => Object.assign(e.currentTarget.style, styles.btnSecondaryHover)}
                    onMouseLeave={e => Object.assign(e.currentTarget.style, styles.btnSecondaryBase)}
                >
                    {content.backButton}
                </button>
                <button
                    onClick={onStart}
                    disabled={loading}
                    style={{
                        ...styles.btnPrimary,
                        flex: 2,
                        opacity: loading ? 0.7 : 1,
                        cursor: loading ? "default" : "pointer",
                    }}
                    onMouseEnter={e => { if (!loading) e.currentTarget.style.background = T.primary400; }}
                    onMouseLeave={e => { if (!loading) e.currentTarget.style.background = T.primary500; }}
                >
                    {loading ? content.startingButton : content.startButton}
                </button>
            </div>
        </>
    );
}

// ─── Shared style objects ────────────────────────────────────────────────────
const styles: Record<string, React.CSSProperties> = {
    iconBox: {
        flexShrink: 0,
        width: 32, height: 32, borderRadius: 8,
        background: T.primary50,
        border: `1px solid ${T.primary100}`,
        display: "flex", alignItems: "center", justifyContent: "center",
    },
    modalTitle: {
        fontSize: 16, fontWeight: 600,
        color: T.neutral900, lineHeight: 1.3,
    },
    stepLabel: {
        fontSize: 11.5, color: T.neutral400, marginTop: 1,
    },
    divider: {
        height: 1, background: T.neutral200,
    },
    btnPrimary: {
        flex: 2, height: 36, borderRadius: 8,
        padding: "0 16px",
        background: "#2563EB", color: "#ffffff",
        border: "none",
        fontSize: 14, fontWeight: 600,
        cursor: "pointer",
        transition: "background 0.15s ease",
        display: "flex", alignItems: "center", justifyContent: "center",
    },
    btnSecondary: {
        flex: 1, height: 36, borderRadius: 8,
        padding: "0 16px",
        background: "#ffffff", color: "#4B5563",
        border: "1px solid #E5E7EB",
        fontSize: 14, fontWeight: 500,
        cursor: "pointer", transition: "all 0.15s ease",
    },
    btnSecondaryBase: {
        background: "#ffffff", borderColor: "#E5E7EB",
    },
    btnSecondaryHover: {
        background: "#F8FAFC", borderColor: "#9CA3AF",
    },
};

// ─── InterviewConfirmationModal ──────────────────────────────────────────────
interface InterviewConfirmationModalProps {
    onClose: () => void;
    onConfirm: () => void;
    id?: string;
    loading?: boolean;
    interviewLanguage?: string;
    language?: "English" | "Vietnamese" | "FirstMove" | "Demo" | "Training";
    colorScheme?: 'default' | 'black';
    companyPreferences?: CompanyPreferences | undefined;
    isShowBrand?: boolean;
    companyName?: string;
}

export function InterviewConfirmationModal({
    onClose,
    onConfirm,
    id,
    loading,
    language = "English",
    interviewLanguage,
    companyName = "ourteam",
}: InterviewConfirmationModalProps) {
    const [step, setStep] = useState<"consent" | "tips">("consent");

    // Resolve language key
    let langKey: LanguageKey = language;
    if (language === "Demo" && interviewLanguage === "id-ID") {
        langKey = "id-ID";
    } else if (language === "Demo" && interviewLanguage === "ms-MY") {
        langKey = "ms-MY";
    }

    const content = getContent(langKey, companyName);

    return (
        <div
            id={id || "confirmation-modal"}
            style={{
                position: "fixed", inset: 0,
                background: T.overlay,
                backdropFilter: "blur(4px)",
                display: "flex", alignItems: "center", justifyContent: "center",
                zIndex: 400,
                padding: 16,
                animation: "ot-fadeIn 0.2s ease",
            }}
        >
            <div
                key={step}
                style={{
                    background: T.white,
                    borderRadius: 16,
                    padding: 24,
                    width: "100%",
                    maxWidth: step === "consent" ? 420 : 460,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.16)",
                    animation: "ot-stepIn 0.18s ease",
                }}
            >
                {step === "consent"
                    ? <ConsentStep content={content} onCancel={onClose} onAgree={() => setStep("tips")} />
                    : <TipsStep content={content} loading={loading} onStart={onConfirm} onBack={() => setStep("consent")} />
                }
            </div>

            <style>{`
                @keyframes ot-fadeIn {
                    from { opacity: 0; }
                    to   { opacity: 1; }
                }
                @keyframes ot-stepIn {
                    from { opacity: 0; transform: translateY(6px) scale(0.98); }
                    to   { opacity: 1; transform: translateY(0)   scale(1); }
                }
            `}</style>
        </div>
    );
}
